import _extends from "@babel/runtime/helpers/extends";
import React, { lazy, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { LazySuspense } from 'react-loosely-lazy';
import styled, { css } from 'styled-components';
import { AnalyticsSource } from '../components/analytics';
import { RenotifyModal } from '../components/PeopleMenu/lazy-renotify-modal';
import { RenotifyStatus, usePendingUsers } from '../hooks/usePendingUsers';
const AKModalTransition = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "async-people-menu-modal-transition-dialog" */'@atlaskit/modal-dialog').then(module => ({
  default: module.ModalTransition
})));
const TeamCreateDialog = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "async-people-menu-team-create-dialog" */'@atlassian/people-teams/teamCreateDialog').then(module => ({
  default: module.TeamCreateDialog
})));

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- needs manual remediation
const resetBoxModel = css`
	width: ${"var(--ds-space-0, 0px)"} !important;
	height: ${"var(--ds-space-0, 0px)"} !important;
	margin: ${"var(--ds-space-0, 0px)"} !important;
	padding: ${"var(--ds-space-0, 0px)"} !important;
`;

// this wrapper is for preventing changing layout of around elements
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogWrapper = styled.div`
	${resetBoxModel};

	&:before,
	&:after {
		${resetBoxModel};
		content: '' !important;
		box-shadow: none !important;
		border-radius: ${"var(--ds-space-0, 0px)"} !important;
	}
`;
const analyticsAttrsTeamCreateDialog = {
  ref: AnalyticsSource.PEOPLE_MENU
};
export const PeopleMenuContext = /*#__PURE__*/React.createContext({
  orgId: '',
  cloudId: '',
  userId: '',
  product: 'confluence',
  testId: '',
  isPeopleMenuOpen: false,
  isSSR: false,
  onClickedItem: () => undefined,
  pushRoute: () => undefined,
  togglePeopleMenu: () => undefined,
  toggleTeamCreateDialog: () => undefined,
  _hasError: undefined,
  teamCreateDialogProps: {},
  renotifyExperimentProps: {
    isLoading: false,
    isEnrolled: false,
    isJswTargeted: false,
    onFetchedPendingUsers: () => false
  },
  showRenotifyMessage: false,
  shouldRenderToParent: false,
  isSectionList: false
});
export function PeopleMenuContextProvider(props) {
  const {
    isSSR = false,
    cloudId,
    renotifyExperimentProps
  } = props;
  const [isOpenTeamCreateDialog, toggleOpenTeamCreateDialog] = useState(false);
  const [renotifyDismissed, setRenotifyDismissed] = useState(false);
  const [isSpotlightActive, setIsSpotlightActive] = useState(undefined);
  const [showRenotifyModal, setShowRenotifyModal] = useState(false);
  const {
    children,
    isOpen,
    onOpen,
    onClose,
    ...otherProps
  } = props;
  const pendingUsersResponse = usePendingUsers({
    cloudId,
    startFetching: isOpen,
    renotifyExperiment: renotifyExperimentProps,
    product: props === null || props === void 0 ? void 0 : props.product
  });
  const renotifyEligible = pendingUsersResponse.status === RenotifyStatus.ELIGIBLE;
  const pendingUsers = renotifyEligible ? pendingUsersResponse.pendingUsers : undefined;
  const showRenotifyMessage = renotifyEligible && !renotifyDismissed;
  const togglePeopleMenu = useCallback(newState => {
    if (newState === isOpen) {
      return;
    }
    if (newState) {
      onOpen();
    } else {
      onClose();
    }
  }, [isOpen, onClose, onOpen]);
  const toggleTeamCreateDialog = useCallback(isOpen => {
    toggleOpenTeamCreateDialog(isOpen);
  }, [toggleOpenTeamCreateDialog]);
  const toggleRenotifyModal = useCallback(() => {
    setShowRenotifyModal(prevState => {
      return !prevState;
    });
  }, []);
  const values = useMemo(() => ({
    teamCreateDialogProps: {},
    ...otherProps,
    // extra props
    isPeopleMenuOpen: isOpen,
    togglePeopleMenu,
    toggleTeamCreateDialog,
    toggleRenotifyModal,
    pendingUsers,
    renotifyEligible,
    showRenotifyMessage,
    setRenotifyDismissed,
    isSpotlightActive,
    setIsSpotlightActive
  }), [otherProps, isOpen, togglePeopleMenu, toggleTeamCreateDialog, toggleRenotifyModal, pendingUsers, renotifyEligible, showRenotifyMessage, setRenotifyDismissed, isSpotlightActive, setIsSpotlightActive]);
  const closeTeamCreateDialog = useCallback(() => {
    var _values$teamCreateDia;
    toggleOpenTeamCreateDialog(false);
    if (typeof ((_values$teamCreateDia = values.teamCreateDialogProps) === null || _values$teamCreateDia === void 0 ? void 0 : _values$teamCreateDia.onClose) === 'function') {
      values.teamCreateDialogProps.onClose();
    }
  }, [toggleOpenTeamCreateDialog, values.teamCreateDialogProps]);
  return /*#__PURE__*/React.createElement(PeopleMenuContext.Provider, {
    value: values
  }, children, !isSSR && isOpenTeamCreateDialog && /*#__PURE__*/React.createElement(DialogWrapper, {
    "data-testid": `${otherProps.testId}-team-create-dialog-wrapper`
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(AKModalTransition, null, isOpenTeamCreateDialog && /*#__PURE__*/React.createElement(TeamCreateDialog, _extends({
    cloudId: values.cloudId,
    orgId: values.orgId,
    product: values.product,
    principalId: values.userId,
    addFlag: values.addFlag,
    pushRoute: values.pushRoute,
    extraAnalyticsAttrs: analyticsAttrsTeamCreateDialog
  }, values.teamCreateDialogProps, {
    onClose: closeTeamCreateDialog
  }))))), !isSSR && showRenotifyModal && /*#__PURE__*/React.createElement(DialogWrapper, null, /*#__PURE__*/React.createElement(LazySuspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(AKModalTransition, null, showRenotifyModal && /*#__PURE__*/React.createElement(RenotifyModal, null)))));
}
export const usePeopleMenuContext = () => useContext(PeopleMenuContext);